import { Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AutoCloudDnsClient } from '../../utils/auto-cloud-dns-client';

/**
 * Interface representing a DNS permission.
 */
export interface IdnsPermission {
  id: number;
  userId: string | number;
  companyId: string | number;
  mainAccess: boolean;
  read: boolean;
  write: boolean;
  createdAt: Date;
  updatedAt: Date;
  domainId: number;
}

/**
 * Class representing a DNS permission.
 */
export class DnsPermission {
  /**
   * Base URL for DNS permission API endpoints.
   */
  static baseUrl = '/permissions';

  /**
   * The ID of the permission.
   */
  id: number;

  /**
   * The ID of the user associated with the permission.
   */
  userId: string | number;

  /**
   * The ID of the company associated with the permission.
   */
  companyId: string | number;

  /**
   * Whether the user has main access to the DNS.
   */
  mainAccess: boolean;

  /**
   * Whether the user has read access to the DNS.
   */
  read: boolean;

  /**
   * Whether the user has write access to the DNS.
   */
  write: boolean;

  /**
   * The date and time the permission was created.
   */
  createdAt: Date;

  /**
   * The date and time the permission was last updated.
   */
  updatedAt: Date;

  /**
   * The ID of the domain associated with the permission.
   */
  domainId: number;

  constructor(data: IdnsPermission) {
    this.id = data.id;
    this.userId = data.userId;
    this.companyId = data.companyId;
    this.mainAccess = data.mainAccess;
    this.read = data.read;
    this.write = data.write;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.domainId = data.domainId;
  }


  /**
   * Retrieves DNS permissions for a specific user.
   *
   * @param userId - The ID of the user to retrieve permissions for.
   * @returns A Promise that resolves to a new DnsPermission instance.
   */
  static async getByUser(userId: string | number) {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeGet<IdnsPermission>(`${DnsPermission.baseUrl}/user/${userId}`);
    const data = await lastValueFrom(observer$);

    return new DnsPermission(data);
  }

  /**
   * Adds or updates DNS permissions for a specific user.
   *
   * @param permissions - The permissions to add or update.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static addUpdate(permissions: Omit<IdnsPermission, 'id' | 'createdAt' | 'updatedAt'>) {
    const observer$ = AutoCloudDnsClient.ehttp
      .makePut<Isuccess>(`${DnsPermission.baseUrl}/${permissions.userId}`, permissions);

    return lastValueFrom(observer$);
  }

  /**
   * Removes DNS permissions for a specific user.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  remove() {
    return AutoCloudDnsClient.ehttp
      .makeDelete<Isuccess>(`${DnsPermission.baseUrl}/${this.userId}`);
  }
}
